import { forwardRef } from "react";
import { motion } from "framer-motion";

import { BACKEND, FRONT_END } from "@/constants";
import ASCIIText from "../ui/text-animation";
import TextType from "../ui/text-type";

interface SkillsProps {
  isInView: boolean;
  fadeInComplete: boolean;
}

export const Skills = forwardRef<HTMLElement, SkillsProps>(
  ({ isInView, fadeInComplete }, ref) => {
    return (
      <section ref={ref} className="py-12 sm:py-20 bg-muted/30">
        <div className="container px-4 sm:px-6 max-w-7xl justify-center mx-auto">
          <motion.div
            key={`skills-${fadeInComplete}`}
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center mb-10 sm:mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Skills
            </h2>
            <div className="h-1 w-16 sm:w-20 bg-primary mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg text-muted-foreground">
              I've worked with a variety of technologies and frameworks to
              create stunning web applications with amazing user experiences.
            </p>
          </motion.div>

          <motion.div
            key={`fe-${fadeInComplete}`}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 sm:mt-16 gap-4 sm:gap-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 text-center"
          >
            <div className="md:w-[200px] flex justify-center items-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 justify-center">
                <TextType
                  typingSpeed={75}
                  pauseDuration={1500}
                  showCursor={false}
                  cursorCharacter="▎"
                  text={"Frontend"}
                  deletingSpeed={50}
                  cursorBlinkDuration={0.5}
                />
              </h2>
            </div>
            {FRONT_END.map((stat, index) => (
              <motion.div
                key={`fe-${index}`}
                initial={{ y: 50, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="p-4 sm:p-6 rounded-lg border bg-card flex gap-1 justify-center items-center"
              >
                <div className="w-[25px] h-[25px]">
                  <img
                    src={`${stat.src.src}`}
                    alt=""
                    className="object-cover w-[25px] h-[25px]"
                  />
                </div>
                <p className="text-sm md:text-xl font-bold text-primary">
                  {stat.name}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            key={`be-${fadeInComplete}`}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-10 sm:mt-16 gap-4 sm:gap-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 text-center grid-container"
          >
            <div className="md:w-[200px] item-1 flex justify-center items-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-0">
                <TextType
                  typingSpeed={75}
                  pauseDuration={1500}
                  showCursor={false}
                  cursorCharacter="▎"
                  text={"Backend"}
                  deletingSpeed={50}
                  cursorBlinkDuration={0.5}
                />
              </h2>
            </div>
            {BACKEND.map((stat, index) => (
              <motion.div
                key={`be-${index}`}
                initial={{ y: 50, opacity: 0 }}
                animate={isInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="p-4 sm:p-6 rounded-lg border bg-card flex gap-1 justify-center items-center"
              >
                <div className="w-[25px] h-[25px]">
                  <img
                    src={`${stat.src.src}`}
                    className="object-cover w-[25px] h-[25px]"
                  />
                </div>
                <p className="text-sm md:text-xl font-bold text-primary">
                  {stat.name}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    );
  },
);

Skills.displayName = "Skills";
